export const mercuryTree = {
  id: 'mercury',
  label: 'Mercury',
  type: 'Terrestrial planet',
  summary: 'The smallest planet and closest to the Sun. A scorched, cratered world with almost no atmosphere.',
  payload: [
    {
      type: 'description',
      text: 'Mercury is the innermost planet, orbiting the Sun every 88 Earth days. Its surface is pocked with craters and looks much like our Moon at first glance, but it has a massive iron core (roughly 70% of the planet\'s mass) and strange features found nowhere else in the solar system. Despite being closest to the Sun, it is not the hottest planet.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '4,879 km (0.383 × Earth)' },
        { label: 'Mass',              value: '3.301 × 10²³ kg (0.055 × Earth)' },
        { label: 'Distance from Sun', value: '57.9 M km (0.387 AU)' },
        { label: 'Orbital period',    value: '88 Earth days' },
        { label: 'Day length',        value: '176 Earth days (solar day)' },
        { label: 'Sidereal rotation', value: '58.6 Earth days' },
        { label: 'Moons',             value: '0' },
        { label: 'Surface temp',      value: '−180 to +430 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Physical properties, gravity, magnetic field',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '2,440.5 km' },
            { label: 'Mean density',      value: '5.429 g/cm³' },
            { label: 'Surface gravity',   value: '3.7 m/s² (0.38 g)' },
            { label: 'Escape velocity',   value: '4.25 km/s' },
            { label: 'Axial tilt',        value: '0.034°' }
          ]
        },
        {
          type: 'stats',
          label: 'Magnetic',
          entries: [
            { label: 'Field strength', value: '~1% of Earth\'s' },
            { label: 'Source',         value: 'Partially molten iron core' },
            { label: 'Geometry',       value: 'Dipole, offset ~20% north' }
          ]
        }
      ]
    },
    {
      id: 'surface',
      label: 'Surface & geology',
      summary: 'Craters, scarps, hollows, and the Caloris Basin',
      payload: [
        {
          type: 'description',
          text: 'Mercury\'s surface is dominated by impact craters accumulated over billions of years. It is crossed by long scarps, cliffs hundreds of kilometers long that formed as the planet cooled and its crust contracted. "Hollows" (shallow, irregular, bright depressions) appear in many craters and may be actively forming today as volatiles sublimate.'
        },
        {
          type: 'stats',
          label: 'Notable features',
          entries: [
            { label: 'Caloris Basin',   value: '1,550 km wide impact crater' },
            { label: 'Rembrandt',       value: '716 km impact basin' },
            { label: 'Discovery Rupes', value: 'About 400 km long scarp' },
            { label: 'Hollows',         value: 'Unique to Mercury' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Permanently shadowed craters at Mercury\'s poles hold water ice, confirmed by radar and MESSENGER neutron spectrometer data. Even on the planet closest to the Sun, there are places sunlight never reaches.'
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Exosphere',
      summary: 'Barely an atmosphere: a thin envelope of atoms kicked off the surface',
      payload: [
        {
          type: 'description',
          text: 'Mercury is too small and too hot to hold a real atmosphere. What it has is an exosphere, a tenuous halo of atoms sputtered off the surface by solar wind and micrometeorite impacts. Individual atoms are more likely to hit the surface again than to collide with each other.'
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Oxygen (O)',    percent: 42,   color: '#7dd3a8' },
            { name: 'Sodium (Na)',   percent: 29,   color: '#ffd966' },
            { name: 'Hydrogen (H)',  percent: 22,   color: '#4a9eff' },
            { name: 'Helium (He)',   percent: 6,    color: '#ffaaaa' },
            { name: 'Potassium (K)', percent: 0.5,  color: '#c099ff' }
          ]
        }
      ]
    },
    {
      id: 'orbit',
      label: 'Orbit & rotation',
      summary: 'The 3:2 resonance and relativistic precession',
      payload: [
        {
          type: 'description',
          text: 'Mercury is locked in a 3:2 spin-orbit resonance: it rotates three times on its axis for every two orbits around the Sun. This means a solar day on Mercury (sunrise to sunrise) lasts 176 Earth days: two full orbits.'
        },
        {
          type: 'stats',
          entries: [
            { label: 'Orbital eccentricity', value: '0.2056 (most elliptical)' },
            { label: 'Perihelion',           value: '46 million km' },
            { label: 'Aphelion',             value: '69.8 million km' },
            { label: 'Mean orbital velocity', value: '47.36 km/s (fastest)' },
            { label: 'Perihelion precession', value: '575 arcsec/century (43 of it from GR)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'The unexplained precession of Mercury\'s orbit was one of the first confirmations of Einstein\'s general relativity. Newtonian gravity couldn\'t account for 43 arcseconds per century, and GR accounted for them perfectly.'
        }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'Only three spacecraft have visited',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Mariner 10',  value: '1974–1975, three flybys (NASA)' },
            { label: 'MESSENGER',   value: '2011–2015, first orbiter (NASA)' },
            { label: 'BepiColombo', value: 'Launched 2018, enters orbit 21 Nov 2026 (ESA/JAXA)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'For an orbiter, Mercury is harder to reach than Saturn. Slowing into orbit means bleeding off the enormous speed gained falling toward the Sun, so BepiColombo made nine planetary flybys over eight years to get there.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',  url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/mercuryfact.html' },
            { label: 'MESSENGER archive', url: 'https://messenger.jhuapl.edu/' },
            { label: 'BepiColombo',       url: 'https://www.esa.int/Science_Exploration/Space_Science/BepiColombo' },
            { label: 'BepiColombo arrival (ESA)', url: 'https://www.esa.int/Science_Exploration/Space_Science/BepiColombo/Latest_updates_BepiColombo_s_arrival_at_Mercury' },
            { label: 'MESSENGER: scarps',  url: 'https://messenger.jhuapl.edu/Learn/features/scarps.html' },
            { label: 'Wikipedia: Mercury', url: 'https://en.wikipedia.org/wiki/Mercury_(planet)' }
          ]
        }
      ]
    }
  ]
};
